/**
 * RoadmapManager — owns roadmap data, filters and the grid/kanban view choice.
 *
 * Loads everything once through `listRoadmapItems` and filters/sorts in the
 * browser: the corpus is small (the callable defaults to 500 items) so a round
 * trip per filter change would buy nothing. Both views render the same filtered
 * set and open the same editor modal.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Button } from '../common/Button';
import { RoadmapEditorModal } from './RoadmapEditorModal';
import { RoadmapFilterBar } from './RoadmapFilterBar';
import { RoadmapGrid } from './RoadmapGrid';
import { RoadmapKanban } from './RoadmapKanban';
import { sortRows, type RoadmapSortDir, type RoadmapSortKey } from './roadmapSort';
import { listRoadmapItems, saveRoadmapItem } from '../../services/firebase';
import { STATUS_LABELS } from './roadmapMeta';
import type {
  RoadmapItem,
  RoadmapOwner,
  RoadmapPriority,
  RoadmapStatus,
} from '../../types';

type ViewMode = 'grid' | 'kanban';

/** Query param carrying the view so a board link is shareable. */
const VIEW_PARAM = 'view';

/**
 * Delay between the last keystroke and re-filtering. Search runs over the full
 * `detailedContent` of every item, so filtering per keystroke would rebuild
 * the grid — and re-sort it — while the user is still typing.
 */
const SEARCH_DEBOUNCE_MS = 200;

export function RoadmapManager() {
  const [items, setItems] = useState<RoadmapItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // `searchInput` tracks the box; `searchQuery` is the debounced, normalised
  // value the list filters on.
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [statusFilter, setStatusFilter] = useState<RoadmapStatus[]>([]);
  const [priorityFilter, setPriorityFilter] = useState<RoadmapPriority[]>([]);
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [ownerFilter, setOwnerFilter] = useState<RoadmapOwner | 'all'>('all');

  const [sortKey, setSortKey] = useState<RoadmapSortKey>('updatedAt');
  const [sortDir, setSortDir] = useState<RoadmapSortDir>('desc');

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<RoadmapItem | null>(null);

  /** Ids with a drag-triggered status write in flight. */
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());

  const [searchParams, setSearchParams] = useSearchParams();
  // Anything other than an explicit 'kanban' is grid — an unknown value in a
  // pasted URL degrades to the default rather than rendering nothing.
  const view: ViewMode =
    searchParams.get(VIEW_PARAM) === 'kanban' ? 'kanban' : 'grid';

  const setView = (next: ViewMode) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'kanban') {
      params.set(VIEW_PARAM, 'kanban');
    } else {
      // Grid is the default — drop the param instead of writing ?view=grid.
      params.delete(VIEW_PARAM);
    }
    // `replace` so toggling views doesn't stack history entries the back button
    // then has to chew through.
    setSearchParams(params, { replace: true });
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listRoadmapItems();
      setItems(rows);
    } catch (err) {
      console.error('Failed to load roadmap:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to load roadmap items',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const handle = setTimeout(
      () => setSearchQuery(searchInput.trim().toLowerCase()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(handle);
  }, [searchInput]);

  /** Every tag present in the data, for the tag filter row. */
  const allTags = useMemo(() => {
    const seen = new Set<string>();
    for (const item of items) {
      for (const tag of item.tags ?? []) seen.add(tag);
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((item) => {
      // Search composes with the chips rather than replacing them: a query
      // narrows whatever the chips already selected.
      if (searchQuery) {
        const haystack = [
          item.title,
          item.description,
          (item.tags ?? []).join(' '),
          // Optional — items seeded before v1.41.0 have no body to match.
          item.detailedContent ?? '',
        ]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(searchQuery)) return false;
      }
      if (statusFilter.length > 0) {
        if (!item.status || !statusFilter.includes(item.status)) return false;
      }
      if (priorityFilter.length > 0) {
        if (!item.priority || !priorityFilter.includes(item.priority)) {
          return false;
        }
      }
      if (ownerFilter !== 'all' && item.owner !== ownerFilter) return false;
      if (tagFilter.length > 0) {
        const tags = item.tags ?? [];
        // OR across selected tags — picking two tags widens, not narrows.
        if (!tagFilter.some((tag) => tags.includes(tag))) return false;
      }
      return true;
    });
  }, [items, searchQuery, statusFilter, priorityFilter, tagFilter, ownerFilter]);

  const sorted = useMemo(
    () => sortRows(filtered, sortKey, sortDir),
    [filtered, sortKey, sortDir],
  );

  const handleSort = (key: RoadmapSortKey) => {
    if (key === sortKey) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    // Dates read best newest-first; everything else best A→Z / P0-first.
    setSortDir(key === 'updatedAt' ? 'desc' : 'asc');
  };

  const toggleIn = <T,>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

  const clearFilters = () => {
    // Clear both halves: leaving the debounce to catch up would flash the
    // unfiltered list back through the search for a frame.
    setSearchInput('');
    setSearchQuery('');
    setStatusFilter([]);
    setPriorityFilter([]);
    setTagFilter([]);
    setOwnerFilter('all');
  };

  const openNew = () => {
    setEditing(null);
    setEditorOpen(true);
  };

  const openEdit = (item: RoadmapItem) => {
    setEditing(item);
    setEditorOpen(true);
  };

  const markSaving = (id: string, saving: boolean) => {
    setSavingIds((prev) => {
      const next = new Set(prev);
      if (saving) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  /**
   * Kanban drop: repaint immediately, then persist. On failure put the card back
   * where it came from — we restore the previous status by id rather than
   * snapshotting the whole list, so a concurrent drag of another card survives
   * the rollback.
   */
  const handleStatusChange = useCallback(
    async (item: RoadmapItem, status: RoadmapStatus): Promise<boolean> => {
      const previous = item.status;
      markSaving(item.id, true);
      setItems((prev) =>
        prev.map((row) => (row.id === item.id ? { ...row, status } : row)),
      );

      try {
        // `saveRoadmapItem` resolves update-vs-create from `id`, and every other
        // field is optional on update — so a bare status patch is the whole call.
        await saveRoadmapItem({ id: item.id, status });
        return true;
      } catch (err) {
        console.error('Failed to update roadmap status:', err);
        setItems((prev) =>
          prev.map((row) =>
            row.id === item.id ? { ...row, status: previous } : row,
          ),
        );
        toast.error(
          err instanceof Error
            ? `Could not move to ${STATUS_LABELS[status] ?? status}: ${err.message}`
            : `Could not move to ${STATUS_LABELS[status] ?? status}`,
        );
        return false;
      } finally {
        markSaving(item.id, false);
      }
    },
    [],
  );

  const toggleClass = (active: boolean) =>
    `px-3 py-1 text-xs font-medium transition-colors ${
      active
        ? 'bg-brand-600 text-white'
        : 'bg-white text-gray-600 hover:bg-gray-50'
    }`;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold text-gray-900">Roadmap</h2>
        <span className="text-sm text-gray-500">
          {items.length} total
          {sorted.length !== items.length && ` · ${sorted.length} shown`}
        </span>

        <div className="ml-auto flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
            <button
              type="button"
              onClick={() => setView('grid')}
              className={toggleClass(view === 'grid')}
              aria-pressed={view === 'grid'}
            >
              Grid
            </button>
            <button
              type="button"
              onClick={() => setView('kanban')}
              className={`${toggleClass(view === 'kanban')} border-l border-gray-300`}
              aria-pressed={view === 'kanban'}
            >
              Kanban
            </button>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void load()}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button variant="primary" size="sm" onClick={openNew}>
            + Add Project
          </Button>
        </div>
      </div>

      <RoadmapFilterBar
        search={searchInput}
        onSearchChange={setSearchInput}
        statusFilter={statusFilter}
        priorityFilter={priorityFilter}
        tagFilter={tagFilter}
        ownerFilter={ownerFilter}
        allTags={allTags}
        onToggleStatus={(s) => setStatusFilter((prev) => toggleIn(prev, s))}
        onTogglePriority={(p) => setPriorityFilter((prev) => toggleIn(prev, p))}
        onToggleTag={(t) => setTagFilter((prev) => toggleIn(prev, t))}
        onOwnerChange={setOwnerFilter}
        onClear={clearFilters}
      />

      {loading ? (
        <div className="text-gray-500 italic py-8 text-center">Loading…</div>
      ) : error ? (
        <div className="border border-red-200 bg-red-50 rounded-lg p-4 text-center space-y-2">
          <p className="text-sm text-red-700">{error}</p>
          <Button variant="secondary" size="sm" onClick={() => void load()}>
            Try again
          </Button>
        </div>
      ) : sorted.length === 0 ? (
        <div className="text-gray-500 italic py-8 text-center border border-dashed border-gray-200 rounded-lg">
          {items.length === 0
            ? 'No roadmap items yet. Add a project to get started.'
            : 'No projects match the current filters.'}
        </div>
      ) : view === 'kanban' ? (
        <RoadmapKanban
          items={sorted}
          onOpen={openEdit}
          onStatusChange={handleStatusChange}
          savingIds={savingIds}
        />
      ) : (
        <RoadmapGrid
          rows={sorted}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={handleSort}
          onOpen={openEdit}
        />
      )}

      <RoadmapEditorModal
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        item={editing}
        onSaved={() => {
          void load();
        }}
      />
    </div>
  );
}
